import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buscarEnPlanilla, planillaDeCliente, ORIGENES } from '../../services/planillaHistoricaService';

const GRIS = '#4b5563';
const caja = { border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 10, padding: '10px 14px' };

export const formatoFecha = (fila) => {
    if (fila.fecha) {
        const [a, m, d] = String(fila.fecha).slice(0, 10).split('-');
        return `${d}/${m}/${a}`;
    }
    return fila.fechaTexto || '—';
};

/** Todas las celdas de la fila tal cual el Excel, con el título de su columna. */
export function CeldasPlanilla({ fila }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, max-content) minmax(0, 1fr)', gap: '4px 12px' }}>
            {(fila.columnas || []).map((c, i) => (
                <div key={`${c.titulo}-${i}`} style={{ display: 'contents' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>{c.titulo}</span>
                    <span style={{ fontSize: '0.85rem', color: '#111827', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{c.valor}</span>
                </div>
            ))}
        </div>
    );
}

/** El resumen de una fila: de qué solapa, qué trámite, cuándo y por qué. */
function Resumen({ fila }) {
    const eliminada = fila.origen === 'CARPETA_ELIMINADA';
    return (
        <div style={{ fontSize: '0.85rem', color: '#111827' }}>
            <span className={`badge ${eliminada ? 'bg-danger-subtle text-danger-emphasis' : 'bg-secondary-subtle text-secondary-emphasis'} me-2`}>
                {eliminada ? 'Carpeta eliminada' : 'Archivo'}
            </span>
            <strong>{fila.tramite || 'Trámite sin nombre'}</strong>
            {' · '}{eliminada ? 'eliminada el ' : 'cobró el '}{formatoFecha(fila)}
            {eliminada && fila.motivo && <> · <span style={{ color: GRIS }}>{fila.motivo}</span></>}
            {!eliminada && fila.arreglo && <> · <span style={{ color: GRIS }}>{fila.arreglo}</span></>}
            {!eliminada && fila.seguimiento && <> · <span style={{ color: '#1e40af', fontWeight: 600 }}>Reajuste: {fila.seguimiento}</span></>}
        </div>
    );
}

function FilaPlegable({ fila, conNombre = false }) {
    const [abierta, setAbierta] = useState(false);
    return (
        <div className="pt-2 mt-2" style={{ borderTop: '1px solid #e2e8f0' }}>
            <div className="d-flex align-items-start justify-content-between gap-2">
                <div>
                    {conNombre && (
                        <div style={{ fontWeight: 600, color: '#111827' }}>
                            {fila.nombre || 'Sin nombre'}
                            <span style={{ color: GRIS, fontWeight: 400 }}> · {fila.cuil || 'sin CUIL'}</span>
                            {fila.esCliente && (
                                <Link to={`/clientes/vista-detallada/${fila.cuil}`} className="ms-2" style={{ fontSize: '0.8rem' }}>
                                    ver cliente
                                </Link>
                            )}
                        </div>
                    )}
                    <Resumen fila={fila} />
                </div>
                <button type="button" onClick={() => setAbierta(!abierta)}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#1e40af', fontSize: '0.8rem', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                    {abierta ? 'Ocultar fila' : 'Ver fila del Excel'}
                </button>
            </div>
            {abierta && <div className="mt-2"><CeldasPlanilla fila={fila} /></div>}
        </div>
    );
}

/**
 * En la ficha del cliente: si su CUIL está en CARPETAS ELIMINADAS o ARCHIVO, lo muestra junto a sus
 * trámites. Si no está, no dibuja nada.
 */
export function PlanillaHistoricaCliente({ cuil }) {
    const [filas, setFilas] = useState([]);
    useEffect(() => {
        let vigente = true;
        setFilas([]);
        Promise.resolve()
            .then(() => planillaDeCliente(cuil))
            .then((d) => vigente && setFilas(d))
            .catch(() => vigente && setFilas([]));
        return () => { vigente = false; };
    }, [cuil]);

    if (filas.length === 0) return null;
    const eliminadas = filas.filter((f) => f.origen === 'CARPETA_ELIMINADA').length;
    const archivo = filas.length - eliminadas;
    return (
        <div style={{ ...caja, margin: '0 0 12px' }}>
            <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>
                <i className="bi bi-archive me-2" />
                Historial en la planilla:{' '}
                {[eliminadas && `${eliminadas} carpeta${eliminadas === 1 ? '' : 's'} eliminada${eliminadas === 1 ? '' : 's'}`,
                    archivo && `${archivo} en archivo`].filter(Boolean).join(' y ')}
            </div>
            {filas.map((f) => <FilaPlegable key={f.id} fila={f} />)}
        </div>
    );
}

/**
 * En la búsqueda de Clientes: quien buscó un nombre o un CUIL ve también si esa persona pasó por el
 * estudio (carpeta eliminada o archivo) aunque no sea cliente del sistema.
 */
export function CoincidenciasPlanilla({ texto }) {
    const [filas, setFilas] = useState([]);
    const [buscando, setBuscando] = useState(false);

    useEffect(() => {
        const t = (texto || '').trim();
        if (t.length < 3) {
            setFilas([]);
            return undefined;
        }
        let vigente = true;
        // Se espera a que deje de tipear: sin esto sale una consulta por tecla.
        const id = setTimeout(() => {
            setBuscando(true);
            Promise.resolve()
                .then(() => buscarEnPlanilla(t, 10))
                .then((d) => vigente && setFilas(d))
                .catch(() => vigente && setFilas([]))
                .finally(() => vigente && setBuscando(false));
        }, 400);
        return () => { vigente = false; clearTimeout(id); };
    }, [texto]);

    if (!buscando && filas.length === 0) return null;
    return (
        <div style={{ ...caja, margin: '12px 0' }}>
            <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>
                <i className="bi bi-archive me-2" />
                En {ORIGENES.CARPETA_ELIMINADA} / {ORIGENES.ARCHIVO}
                <span style={{ color: GRIS, fontWeight: 400 }}> (no son clientes del sistema, salvo que diga “ver cliente”)</span>
            </div>
            {buscando && filas.length === 0 && <div style={{ fontSize: '0.85rem', color: GRIS }}>Buscando…</div>}
            {filas.map((f) => <FilaPlegable key={f.id} fila={f} conNombre />)}
        </div>
    );
}

/** "Ya fue cliente": el aviso al registrar a alguien cuyo CUIL está en la planilla. */
export function AvisoYaFueCliente({ cuil }) {
    const [filas, setFilas] = useState([]);
    useEffect(() => {
        const limpio = String(cuil || '').replace(/\D/g, '');
        if (limpio.length !== 11) {
            setFilas([]);
            return undefined;
        }
        let vigente = true;
        Promise.resolve()
            .then(() => planillaDeCliente(limpio))
            .then((d) => vigente && setFilas(d))
            .catch(() => vigente && setFilas([]));
        return () => { vigente = false; };
    }, [cuil]);

    if (filas.length === 0) return null;
    const ultima = filas[0];
    return (
        <div className="alert alert-warning py-2 px-3 my-2" style={{ fontSize: '0.85rem' }}>
            <i className="bi bi-archive me-2" />
            <strong>Ya pasó por el estudio</strong> ({ultima.nombre || 'sin nombre'}):{' '}
            {filas.map((f) => `${f.origen === 'CARPETA_ELIMINADA' ? 'carpeta eliminada' : 'archivo'} — ${f.tramite || 'trámite'} (${formatoFecha(f)})`).join('; ')}.
        </div>
    );
}
